import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Heart, Instagram, Mail } from "lucide-react";
import { LANGS, useI18n, ASSETS, ORG } from "@/lib/i18n";
import CookieBanner from "@/components/CookieBanner";
import FloatingCTA from "@/components/FloatingCTA";
import Header from "@/components/Header";
import CampaignBar from "@/components/CampaignBar";
import { api } from "@/lib/api";

function LangSync() {
  const { lang } = useI18n();
  useEffect(() => { document.documentElement.lang = lang || "da"; }, [lang]);
  return null;
}

// Footer, top-bar and cookie banner text all come from /api/site-settings.
// Any admin change publishes immediately without a code deploy.
export default function Layout({ children }) {
  const { t, lang } = useI18n();
  const loc = useLocation();
  const isHome = loc.pathname === "/";
  const [hasStories, setHasStories] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/content", { params: { kind: "story", status: "published" } })
      .then(r => setHasStories(Array.isArray(r.data) && r.data.length > 0))
      .catch(() => setHasStories(false));
    api.get("/site-settings").then(r => setSettings(r.data)).catch(() => setSettings(null));
  }, []);

  // Apply CMS-managed favicon dynamically. Keeps a single authoritative
  // icon link in the <head> with the correct MIME type (SVG → image/svg+xml,
  // PNG → image/png, ICO → image/x-icon) so browsers never silently fall
  // back to the SPA HTML when the type attribute lies about the file.
  useEffect(() => {
    const fav = settings?.brand_assets?.favicon;
    if (!fav) return;
    const ext = fav.split("?")[0].split(".").pop().toLowerCase();
    const mime = ({ svg: "image/svg+xml", png: "image/png", ico: "image/x-icon",
                     jpg: "image/jpeg", jpeg: "image/jpeg", gif: "image/gif",
                     webp: "image/webp" })[ext] || "";
    // Remove every existing icon link so we never leave a stale/duplicate
    // reference competing with the CMS-managed one.
    document.querySelectorAll("link[rel~='icon']").forEach(n => n.parentNode && n.parentNode.removeChild(n));
    const link = document.createElement("link");
    link.setAttribute("rel", "icon");
    link.setAttribute("href", fav);
    if (mime) link.setAttribute("type", mime);
    document.head.appendChild(link);
  }, [settings?.brand_assets?.favicon]);

  // Also hide /stories from footer when the CMS `stories_settings` state is
  // not live. Additive to the "no stories published" fallback below.
  const [storiesLive, setStoriesLive] = useState(true);
  useEffect(() => {
    api.get("/page/stories").then(r => setStoriesLive(!!(r.data && r.data.enabled))).catch(() => setStoriesLive(true));
  }, []);

  const T = (obj) => (obj && (obj[lang] || obj.en || obj.da)) || "";
  const footer = settings?.footer;
  const contact = settings?.contact;
  const socials = settings?.socials;
  const brand = settings?.brand_assets;
  const footerLogo = brand?.footer_logo || "/images/logo-full.webp";
  const logoAlt = (brand?.logo_alt && (brand.logo_alt[lang] || brand.logo_alt.en)) || "DANAmark";

  const footerShow = !footer || footer.show !== false;
  const tagline = T(footer?.tagline) || t("footer_tagline");
  const closing = T(footer?.closing_line) || t("footer_closing");
  const legalLabel = T(footer?.legal_label) || t("footer_legal");
  const contactLabel = T(footer?.contact_label) || t("footer_contact");
  const email = contact?.email || ORG.email;
  const cvr = contact?.cvr || ORG.cvr;
  // Footer location is now entirely CMS-driven (`contact.location_label`).
  // No hardcoded fallback — if the editor clears it, the footer hides it.
  const address = T(contact?.location_label);
  const igHandle = socials?.instagram_handle || ORG.instagram;
  const igUrl = socials?.instagram_url || ORG.instagramUrl;

  // Resolve nav groups from CMS → fallback. Sort by order, filter show=false.
  const navGroups = (settings?.footer_nav_groups && settings.footer_nav_groups.length
    ? settings.footer_nav_groups
    : DEFAULT_FOOTER_GROUPS())
    .filter(g => g && g.show !== false)
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map(g => ({
      ...g,
      links: (g.links || []).filter(l => l && l.show !== false).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    }));

  // Legal links keep the legacy footer.legal_links shape (phase-1 preserved).
  const legalLinks = (footer?.legal_links || DEFAULT_LEGAL_LINKS(t)).filter(l => l && l.show !== false);

  return (
    <div className="min-h-screen bg-base text-ink">
      <Header />
      {/* Offset for the fixed header (h-[68px] mobile / h-[76px] desktop) */}
      <main className="pt-[68px] md:pt-[76px]">
        <CampaignBar />
        {children}
      </main>

      {footerShow && (
        <footer className="mt-24 border-t border-line bg-base" data-testid="site-footer">
          <div className="max-editorial container-x py-16 grid md:grid-cols-5 gap-10">
            <div className="md:col-span-2">
              <Link to="/" className="inline-flex items-center" aria-label="DANAmark home">
                <img src={footerLogo} alt={logoAlt} className="h-12 w-auto" />
              </Link>
              <p className="mt-4 text-sm text-ink/70 leading-relaxed max-w-xs">{tagline}</p>
              <div className="mt-5 flex items-center gap-4">
                <a href={`mailto:${email}`} data-testid="footer-email"
                   className="inline-flex items-center gap-2 text-sm text-ink/70 hover:text-terracotta">
                  <Mail size={14} strokeWidth={1.6} /> {email}
                </a>
              </div>
              {igUrl && (
                <div className="mt-2">
                  <a href={igUrl} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram"
                     className="inline-flex items-center gap-2 text-sm text-ink/70 hover:text-terracotta">
                    <Instagram size={14} strokeWidth={1.6} /> {igHandle}
                  </a>
                </div>
              )}
              <div className="mt-2 label-mono text-ink/50">CVR {cvr}</div>
            </div>

            {/* CMS-driven navigation groups (Explore, Participate, …). */}
            {navGroups.map((g) => (
              <div key={g.id} className="text-sm" data-testid={`footer-group-${g.id}`}>
                <div className="label-mono mb-3 text-ink/60">{T(g.label)}</div>
                <ul className="space-y-2">
                  {g.links.map((l) => (
                    <li key={l.id || l.url}>
                      {l.external
                        ? <a href={l.url} target="_blank" rel="noreferrer" data-testid={`footer-link-${l.id || l.url}`} className="hover:text-terracotta">{T(l.label)}</a>
                        : <Link to={l.url} data-testid={`footer-link-${l.id || l.url}`} className="hover:text-terracotta">{T(l.label)}</Link>}
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {/* Legal (preserved from phase one, still CMS-driven via footer.legal_links). */}
            <div className="text-sm" data-testid="footer-group-legal">
              <div className="label-mono mb-3 text-ink/60">{legalLabel}</div>
              <ul className="space-y-2">
                {legalLinks.map((l, i) => (
                  <li key={i}><Link to={l.url} className="hover:text-terracotta">{T(l.label)}</Link></li>
                ))}
              </ul>
            </div>

            {/* The previously redundant CONTACT block (duplicated email,
                duplicated CVR and the obsolete "Fælledparken · København"
                location) has been removed. Email + Instagram + CVR already
                appear below the logo above. The underlying CMS fields
                (`settings.contact.email`, `.instagram_handle`,
                `.instagram_url`, `.location_label`, `.cvr`) are kept intact
                so the admin can still edit them from Settings. */}
          </div>
          <div className="hairline">
            <div className="max-editorial container-x py-6 flex flex-col md:flex-row justify-between items-center gap-2 text-xs text-ink/50">
              <span>© {new Date().getFullYear()} DANAmark Solidaritet · CVR {cvr}</span>
              <div className="flex items-center gap-4">
                <span>{closing}</span>
                <Link to="/admin" data-testid="footer-admin-link" className="label-mono text-ink/40 hover:text-terracotta">Editor</Link>
              </div>
            </div>
          </div>
        </footer>
      )}

      <CookieBanner />
      <FloatingCTA />
      <LangSync />
    </div>
  );
}

function DEFAULT_FOOTER_GROUPS() {
  return [
    { id: "explore", order: 10, show: true,
      label: { en: "Explore", da: "Udforsk", es: "Explorar" },
      links: [
        { id: "f_story", url: "/mission", order: 10, show: true, label: { en: "Our Story", da: "Vores historie", es: "Nuestra historia" } },
        { id: "f_events", url: "/events", order: 20, show: true, label: { en: "Events", da: "Events", es: "Eventos" } },
        { id: "f_impact", url: "/impact", order: 30, show: true, label: { en: "Impact", da: "Aftryk", es: "Impacto" } },
        { id: "f_involved", url: "/get-involved", order: 40, show: true, label: { en: "Get Involved", da: "Bliv en del af os", es: "Únete" } },
        { id: "f_about", url: "/about", order: 50, show: true, label: { en: "About", da: "Om os", es: "Sobre nosotros" } },
      ] },
    { id: "participate", order: 20, show: true,
      label: { en: "Participate", da: "Deltag", es: "Participa" },
      links: [
        { id: "f_raffle", url: "/raffle", order: 10, show: true, label: { en: "T-shirts", da: "T-shirts", es: "Camisetas" } },
        { id: "f_join", url: "/join", order: 20, show: true, label: { en: "Become a member", da: "Bliv medlem", es: "Hazte miembro" } },
        { id: "f_volunteer", url: "/volunteer", order: 30, show: true, label: { en: "Volunteer", da: "Bliv frivillig", es: "Sé voluntario" } },
        { id: "f_partnership", url: "/partnership", order: 40, show: true, label: { en: "Partner with us", da: "Bliv partner", es: "Colabora con nosotros" } },
      ] },
  ];
}

function DEFAULT_LEGAL_LINKS(t) {
  return [
    { url: "/legal/privacy", label: { en: "Privacy Policy", da: "Privatlivspolitik", es: "Política de privacidad" } },
    { url: "/legal/terms", label: { en: "Terms", da: "Vilkår", es: "Términos" } },
    { url: "/legal/cookies", label: { en: "Cookies", da: "Cookies", es: "Cookies" } },
    { url: "/legal/transparency", label: { en: "Transparency", da: "Åbenhed", es: "Transparencia" } },
    { url: "/legal/donation-terms", label: { en: "Donation Terms", da: "Donationsvilkår", es: "Términos de donación" } },
    { url: "/legal/whistleblower", label: { en: "Whistleblower", da: "Whistleblower", es: "Whistleblower" } },
  ];
}
